import mapboxgl from 'mapbox-gl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import treePhotoFallback from '@/assets/img/noTreeImg.png';
import forestPng from '@/assets/svg/tree.png';
import forestsPng from '@/assets/svg/trees.png';
import { useClusterTooltip } from '../../hooks/useClusterTooltip';
import { useGroupRipple } from '../../hooks/useGroupRipple';
import { useMapData } from '../../hooks/useMapData';
import { useMapEvents } from '../../hooks/useMapEvents';
import { useMapImages } from '../../hooks/useMapImages';
import { useMapLayers } from '../../hooks/useMapLayers';
import { useMapState } from '../../hooks/useMapState';
import { useMarkerPulse } from '../../hooks/useMarkerPulse';
import { Cards } from '../Cards/Cards';
import {
  boundsOfOutlines,
  forestFitPadding,
  isMizanZoneCard,
  TREE_PIN_MAX_ZOOM,
  treeFlyTarget,
} from '../Cards/outlineBounds';
import { FarmerModal } from '../FarmerModal/FarmerModal';
import { Header } from '../Header';
import { LoaderAnimation } from '../LoaderAnimation/LoaderAnimation';
import { Project } from '../Project/Project';
import { Container, MapContainer } from './Dashboard.styles';
import {
  FactoriesGroupLayer,
  FarmersLayer,
  FarmetsGroupLayer,
  ForestsGroupLayer,
  ForestsLayer,
  OutlineLayer,
} from './Layers';
import { MapHint, useMapHint } from './MapHint';
import { PanelsToggle } from './PanelsToggle';
import { getImpactQuery, getPlantingZonesQuery, getTreeExtentsQuery } from './queries';
import { Group } from './types';
import { getTransformedData } from './utils';

// Forest legend entry: a real button (keyboard + screen-reader reachable)
// that zooms the map to its layer. 2026-09-17 (founder): the earlier show/hide
// toggle was replaced - entries zoom, they never hide data.
const LegendZoomButton = ({ label, onZoom }: { label: string; onZoom: () => void }) => (
  <button
    type="button"
    title={`Zoom to: ${label}`}
    onClick={onZoom}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      width: '100%',
      minHeight: 28,
      padding: '2px 0',
      background: 'none',
      border: 'none',
      color: 'white',
      font: 'inherit',
      textAlign: 'left',
      cursor: 'pointer',
    }}
  >
    <span
      style={{
        width: 14,
        height: 10,
        background: 'rgba(255,217,0,0.25)',
        border: '2px solid #ffd900',
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
    <span style={{ flex: 1 }}>{label}</span>
  </button>
);

function formatPlantingDate(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

export const Dashboard: React.FC = () => {
  //this is a hack so header is working, when passing data to the header hooks rules are getting triggered for some reason
  getImpactQuery();

  const mapRef = useRef<MapRef>(null);
  const projectSectionRef = useRef<HTMLDivElement | null>(null);

  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [type, setType] = useState<'factories' | 'farmers' | 'forests' | ''>('');
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  // Founder 2026-09-20: map-only view toggle (PanelsToggle).
  const [panelsHidden, setPanelsHidden] = useState(false);

  // Switching the top category tab must reset any drill-down. The map's detail
  // layers key off selectedGroup.type, not `type` (see useMapLayers), and the
  // overview *Group layers require !selectedGroup. So if a card has been clicked
  // (selectedGroup set) and the user then switches tabs, the header/cards follow
  // `type` but the map keeps rendering the previous category's markers. Clearing
  // selectedGroup (and any open farmer modal) on tab change puts the map back to
  // the new category's overview markers. (2026-08-27)
  const handleTypeChange = useCallback((value: 'factories' | 'farmers' | 'forests' | '') => {
    setSelectedGroup(null);
    setSelectedFarmer(null);
    setType(value);
  }, []);

  const {
    farmersGroupData,
    forestsGroupData,
    farmersData,
    treesData,
    factoriesData,
    isInitialDataLoaded,
    isFullyLoaded,
    isTreesLoading,
  } = useMapData();
  const { viewState, setViewState, isStyleLoaded, setIsStyleLoaded } = useMapState();
  const { handleMapClick, handleZoomOut } = useMapEvents({
    mapRef,
    selectedGroup,
    setSelectedGroup,
    type,
    setSelectedFarmer,
  });
  const { loadAllMapImages, setupMapCursor } = useMapImages();

  // Memoised: a fresh array on every render (every map move) would re-upload the
  // source and re-cluster it. (2026-09-17)
  const transformedFarmers = useMemo(() => getTransformedData(farmersData), [farmersData]);

  // D16a - forest outline layers, shown only in forests context. Each query
  // fails independently and visibly (console warning + no outline); an
  // unpublished tree_project_extents simply means only the Mizan zones draw.
  const { data: treeExtentsData } = getTreeExtentsQuery();
  const { data: mizan1ZonesData } = getPlantingZonesQuery('mizan1zones');
  // Disabled in place 2026-09-17 (founder: single zone only). mizan1zones is the
  // north-western block that holds the recorded-tree extent (checked
  // geometrically: 18 of the extent's 33 vertices fall inside mizan1zones, 0
  // inside mizan2). The south-eastern grid is no longer fetched, drawn, or
  // part of any fitBounds union. getPlantingZonesQuery still accepts it.
  //   const { data: mizan2ZonesData } = getPlantingZonesQuery('mizan2_zone_data_public');
  const isForestsContext = type === 'forests' || selectedGroup?.type === 'forests';
  // Founder decision 2026-09-19 (card views): the Mizan zone polygon, the
  // recorded-tree extent and their legend belong to the "100% Deforestation
  // free" card ONLY. That card shows no tree pins; the other three forests
  // cards show pins and no polygon/legend. Same title match as the card's
  // fitBounds (outlineBounds.ts isMizanZoneCard - forest_group_summary has no
  // stable id). The outline data is still fetched in any forests context so
  // the card click can fit to it without waiting.
  const isMizanZoneCardSelected =
    selectedGroup?.type === 'forests' && isMizanZoneCard(selectedGroup.group);
  const hasZoneOutlines = Boolean(mizan1ZonesData?.features?.length);
  const hasExtentOutlines = Boolean(treeExtentsData?.features?.length);
  // All outline features together, for the forests-card fitBounds in Cards.
  const forestOutlineFeatures = useMemo(
    () => [...(treeExtentsData?.features ?? []), ...(mizan1ZonesData?.features ?? [])],
    [treeExtentsData, mizan1ZonesData],
  );

  // Reverted 2026-09-17 (founder: over-applied). Opening the FORESTS tab no
  // longer moves the map on its own - only a card whose own centroid falls
  // inside the Mizan outline (matched in Cards.tsx via
  // boundsOfOutlinesContaining) fits to it; every other card keeps the
  // pre-D16a onFlyToTrees behaviour. Superseded effect, kept for history:
  //   const forestBounds = useMemo(() => boundsOfOutlines(forestOutlineFeatures), [forestOutlineFeatures]);
  //   useEffect(() => {
  //     if (type !== 'forests' || !isStyleLoaded || !forestBounds) return;
  //     mapRef.current?.getMap()?.fitBounds(forestBounds, {
  //       padding: forestFitPadding(window.innerWidth),
  //       maxZoom: FOREST_OPENING_MAX_ZOOM,
  //       duration: 1500,
  //     });
  //   }, [type, isStyleLoaded, forestBounds, mapRef]);

  // Clickable legend: each entry fits the map to its own layer's bounds, with
  // the same overlay-aware padding as the tab/subcard fit.
  const zoomToOutlines = useCallback(
    (features: Parameters<typeof boundsOfOutlines>[0]) => {
      const bounds = boundsOfOutlines(features);
      if (!bounds) return;
      mapRef.current
        ?.getMap()
        ?.fitBounds(bounds, { padding: forestFitPadding(window.innerWidth), duration: 1500 });
    },
    [mapRef],
  );

  const transformedTrees = useMemo(() => getTransformedData(treesData), [treesData]);

  const filteredTrees = useMemo(
    () =>
      transformedTrees?.filter(
        (f) => typeof f.properties?.photo === 'string' && f.properties.photo.startsWith('http'),
      ) ?? [],
    [transformedTrees],
  );

  // Founder decisions 2026-09-18/19: a tree-pin card fits the map to ITS
  // project's pins within 100 km of the card's own coordinates (bbox,
  // padded, maxZoom 17); none in range -> the card's coordinates at zoom 12. The rule lives in
  // treeFlyTarget (outlineBounds.ts), unit-tested against the real fixtures.
  // Without an anchor (a card row with null geometry) the original all-pins
  // behaviour still applies. Trees Ethiopia -> 'Limu Outgrowers', Farm
  // sequestration -> 'Mizan 1 Ingrowers'; Biocompost in Kericho has no
  // trees and lands on Kericho itself.
  const runTreeFly = useCallback(
    (farmerGroup: string | undefined, anchor: [number, number] | null, landingZoom?: number) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      const target = treeFlyTarget(filteredTrees ?? [], farmerGroup, anchor, landingZoom);
      if (!target) return;
      if (target.kind === 'land') {
        // Founder 2026-09-21 (map-ux-4): land at the card's landing zoom centred
        // on the densest pocket of the project's pins, with the SAME overlay-aware
        // padding as the fits so the centre sits in the clear area between the
        // tab/stat block and the cards (flyTo's padding shifts the vanishing
        // point). "Trees, Ethiopia" lands at 16.25 (dots), "Farm sequestration"
        // at 16.9 (pins) - same pocket, different zoom.
        map.flyTo({
          center: target.center,
          zoom: target.zoom,
          padding: forestFitPadding(window.innerWidth),
          speed: 1.5,
          essential: true,
        });
        return;
      }
      if (target.kind === 'fit') {
        // Founder 2026-09-19 (final): fit the pins' bbox, overlay-aware
        // padding, capped at 17 - so the spread-out Limu pins land in frame.
        map.fitBounds(target.bounds, {
          padding: forestFitPadding(window.innerWidth),
          maxZoom: TREE_PIN_MAX_ZOOM,
          duration: 1500,
        });
        return;
      }
      map.flyTo({ center: target.center, zoom: target.zoom, speed: 1.5, essential: true });
    },
    [filteredTrees, mapRef],
  );

  // Founder decision 2026-09-21 (Question 33e): a tree-card click made while the
  // four per-project tree fetches are still in flight WAITS for them and then
  // fits the pins, instead of landing on the card's coordinates at zoom 12
  // because no pin had arrived yet. That zoom 12 landing is kept only as the
  // fallback for when the fetches finish with no pins (all four failed, or
  // none within 100 km). One pending click at a time; a newer click replaces
  // an older one.
  const pendingTreeFly = useRef<{
    farmerGroup: string | undefined;
    anchor: [number, number] | null;
    landingZoom?: number;
  } | null>(null);
  const flyToTrees = useCallback(
    (farmerGroup?: string, anchor: [number, number] | null = null, landingZoom?: number) => {
      if (isTreesLoading) {
        pendingTreeFly.current = { farmerGroup, anchor, landingZoom };
        return;
      }
      pendingTreeFly.current = null;
      runTreeFly(farmerGroup, anchor, landingZoom);
    },
    [isTreesLoading, runTreeFly],
  );
  useEffect(() => {
    if (isTreesLoading || !pendingTreeFly.current) return;
    const { farmerGroup, anchor, landingZoom } = pendingTreeFly.current;
    pendingTreeFly.current = null;
    runTreeFly(farmerGroup, anchor, landingZoom);
  }, [isTreesLoading, runTreeFly]);

  useEffect(() => {
    if (!isStyleLoaded || selectedGroup?.type !== 'farmers') return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 15,
      anchor: 'bottom',
      maxWidth: '200px',
    });

    const onEnter = (e: mapboxgl.MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = 'pointer';
      const feature = e.features?.[0];
      if (!feature) return;

      const props = feature.properties ?? {};
      const geometry = feature.geometry;
      if (geometry?.type !== 'Point') return;

      const coordinates = (geometry as GeoJSON.Point).coordinates as [number, number];
      const name =
        props.first_name && props.last_name ? `${props.first_name} ${props.last_name}` : '—';
      const location = props.location ?? props.village ?? props.region ?? '—';
      const photo = props.profile_photo ?? '';
      const since = props.registration_date
        ? new Date(props.registration_date).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
          })
        : '';

      if (!photo || !photo.startsWith('http')) return;

      popup
        .setLngLat(coordinates)
        .setHTML(
          `
    <div style="padding:12px;background:white;font-size:14px;">
      <img src="${photo}" style="width:100%;height:140px;object-fit:cover;border-radius:12px 12px 0 0;display:block;" />
      <div style="margin-top:8px;"><strong>${name}</strong></div>
      <div style="margin-top:6px;color:#555;font-size:12px;">📍 ${location}</div>
      ${since ? `<div style="margin-top:4px;color:#555;font-size:12px;">📅 Registered: ${since}</div>` : ''}
    </div>
  `,
        )
        .addTo(map);
    };

    const onLeave = () => {
      map.getCanvas().style.cursor = '';
      popup.remove();
    };

    map.on('mouseenter', 'farmers-marker', onEnter);
    map.on('mouseleave', 'farmers-marker', onLeave);

    return () => {
      map.off('mouseenter', 'farmers-marker', onEnter);
      map.off('mouseleave', 'farmers-marker', onLeave);
      popup.remove();
    };
  }, [isStyleLoaded, selectedGroup, mapRef]);

  useEffect(() => {
    const id = 'moyee-popup-styles';
    if (document.getElementById(id)) {
      return;
    }
    const el = document.createElement('style');
    el.id = id;
    el.textContent = `
      .mapboxgl-popup-content {
        padding: 0 !important;
        border-radius: 16px !important;
        box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
        overflow: hidden !important;
        width: 200px !important;
      }
      .mapboxgl-popup-tip {
        border-top-color: white !important;
      }
    `;
    document.head.appendChild(el);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  useEffect(() => {
    if (!isStyleLoaded || selectedGroup?.type !== 'forests') {
      return;
    }
    const map = mapRef.current?.getMap();
    if (!map) {
      return;
    }

    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 15,
      anchor: 'bottom',
      maxWidth: '200px',
    });

    // 2026-09-21: the yellow single-tree dots (below the pin zoom) show the
    // same photo popup as the tree pins above them.
    const treeLayers = ['forests-marker', 'forests-dots'];

    // Founder 2026-09-21 (trees-2): the popup opens on hover AND on click / touch
    // tap. A click/tap "pins" it so it survives the mouseleave (a hover away no
    // longer closes it); a tap on empty map closes it. On touch there is no
    // hover, so the click path is what opens it there.
    let pinned = false;

    const cardHTMLFor = (feature: mapboxgl.GeoJSONFeature): string | null => {
      const props = feature.properties ?? {};
      const fid = props.fid != null ? String(props.fid) : '—';
      const location = props.location != null ? String(props.location) : 'Unknown location';

      const treesCollection = treesData as GeoJSON.FeatureCollection | undefined;
      const fullFeature = treesCollection?.features?.find(
        (f) => String(f.properties?.fid) === String(fid),
      );
      const photoRaw = fullFeature?.properties?.photo ?? props.photo ?? '';
      const photo = typeof photoRaw === 'string' ? photoRaw : String(photoRaw ?? '');
      if (!photo || !photo.startsWith('http')) {
        return null;
      }

      const plantingDate = formatPlantingDate(props.planting_date);

      // Item 3: on image load failure (many upstream URLs 404 - see item 1) swap
      // to the "Coming soon" placeholder. `this.onerror=null` stops any loop if
      // the placeholder itself failed. The neutral background avoids a flash of
      // broken-image icon while the (large) original downloads.
      return `
        <div style="padding:12px;background:white;font-size:14px;">
          <img src="${escapeHtml(photo)}" alt="Tree ${escapeHtml(fid)}" onerror="this.onerror=null;this.src='${treePhotoFallback}';" style="width:100%;height:140px;object-fit:cover;border-radius:12px 12px 0 0;display:block;background:#e8e6e1;" />
          <div style="margin-top:12px;"><strong>Tree #${escapeHtml(fid)}</strong></div>
          <div style="margin-top:8px;color:#555;font-size:12px;">📍 ${escapeHtml(location)}</div>
          ${
            plantingDate
              ? `<div style="margin-top:4px;color:#555;font-size:12px;">🌱 Planted: ${escapeHtml(plantingDate)}</div>`
              : ''
          }
        </div>
      `;
    };

    const showFor = (feature: mapboxgl.GeoJSONFeature) => {
      if (feature.geometry?.type !== 'Point') return;
      const html = cardHTMLFor(feature);
      if (!html) return;
      const coordinates = (feature.geometry as GeoJSON.Point).coordinates as [number, number];
      popup.setLngLat(coordinates).setHTML(html).addTo(map);
    };

    const onEnter = (e: mapboxgl.MapLayerMouseEvent) => {
      const feature = e.features?.[0];
      if (!feature) return;
      map.getCanvas().style.cursor = 'pointer';
      showFor(feature);
    };

    const onLeave = () => {
      map.getCanvas().style.cursor = '';
      if (!pinned) popup.remove();
    };

    const onClickMarker = (e: mapboxgl.MapLayerMouseEvent) => {
      const feature = e.features?.[0];
      if (!feature) return;
      pinned = true;
      showFor(feature);
    };

    // A tap/click that misses every tree marker closes a pinned popup.
    const onMapClick = (e: mapboxgl.MapMouseEvent) => {
      const hits = map.queryRenderedFeatures(e.point, {
        layers: treeLayers.filter((l) => map.getLayer(l)),
      });
      if (hits.length === 0) {
        pinned = false;
        popup.remove();
      }
    };

    for (const layer of treeLayers) {
      map.on('mouseenter', layer, onEnter);
      map.on('mouseleave', layer, onLeave);
      map.on('click', layer, onClickMarker);
    }
    map.on('click', onMapClick);

    return () => {
      for (const layer of treeLayers) {
        map.off('mouseenter', layer, onEnter);
        map.off('mouseleave', layer, onLeave);
        map.off('click', layer, onClickMarker);
      }
      map.off('click', onMapClick);
      popup.remove();
    };
  }, [isStyleLoaded, selectedGroup, treesData, mapRef]);

  const {
    isFarmersGroup,
    isFactoriesGroup,
    isForestsGroup,
    isFarmers,
    isForests,
    isFactories,
    isSpecificForests,
  } = useMapLayers({
    isStyleLoaded,
    type,
    selectedGroup,
  });
  // Map UX quest (2026-09-17): halo pulse + desktop cluster-count tooltip run
  // only while a clustered pin layer is mounted. The deforestation card mounts
  // no tree pins (founder 2026-09-19), so neither hook runs there.
  const showTreePins = isForests && !isMizanZoneCardSelected;
  const clusteredKind =
    isFullyLoaded && isFarmers && farmersData
      ? 'farmers'
      : showTreePins && treesData
        ? 'forests'
        : null;
  const hasClusteredPins = clusteredKind !== null;
  useMarkerPulse(mapRef, clusteredKind);
  useClusterTooltip(mapRef, hasClusteredPins);
  // Founder 2026-09-21 (round 2): the forests / farmers group markers ripple
  // their ring on hover / selection, the same as the pins. Runs once the map
  // is ready; it attaches to the group layers whenever they are mounted.
  useGroupRipple(mapRef, isInitialDataLoaded && isStyleLoaded);

  const { dismissed: hintDismissed, dismiss: dismissHint } = useMapHint();
  // No hint in factories context: it has neither farmers nor trees to tap, and
  // the founder's wording covers only those two (§10 - text must match content).
  const hintVariant =
    type === 'factories' || selectedGroup?.type === 'factories'
      ? null
      : type === 'forests' || selectedGroup?.type === 'forests'
        ? 'forests'
        : 'farmers';

  const handleMapLoad = () => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    // Test hook for Playwright (inspect map sources/zoom). Never in the
    // production bundle: MODE is baked at build time and prod builds skip this.
    if (import.meta.env.MODE !== 'prod') {
      (window as unknown as { __dashboardMap?: unknown }).__dashboardMap = map;
    }

    const init = () => {
      loadAllMapImages(map).then(() => {
        setIsStyleLoaded(true);
        setupMapCursor(map);
      });
    };

    if (map.isStyleLoaded()) {
      init();
    } else {
      map.on('style.load', init);
    }
  };

  useEffect(() => {
    if (selectedGroup && projectSectionRef.current) {
      const offsetTop = projectSectionRef.current.getBoundingClientRect().top + window.scrollY;
      const halfScreenHeight = window.innerHeight / 2;
      window.scrollTo({ top: offsetTop - halfScreenHeight, behavior: 'smooth' });
    }
  }, [selectedGroup]);

  const mapLoadingMessage =
    selectedGroup?.type === 'forests'
      ? 'Loading trees...'
      : selectedGroup?.type === 'farmers'
        ? 'Loading farmers...'
        : 'Loading...';

  if (!isInitialDataLoaded) {
    return <LoaderAnimation />;
  }

  const parsedFarmersData =
    typeof farmersData === 'string'
      ? (() => {
          try {
            return JSON.parse(farmersData as unknown as string);
          } catch {
            return undefined;
          }
        })()
      : farmersData;
  return (
    <Container>
      <MapContainer>
        {selectedFarmer && (
          <FarmerModal data={selectedFarmer} onClose={() => setSelectedFarmer(null)} />
        )}
        {/* Founder 2026-09-20: map-only view. The tabs (with their summary
            strip) and the card row are hidden with CSS, never unmounted, so
            their state and the map's view and layers are untouched. */}
        <div
          data-testid="map-panels"
          style={{ display: panelsHidden ? 'none' : 'contents' }}
          aria-hidden={panelsHidden}
        >
          <Header type={type} setType={handleTypeChange} onZoomOut={handleZoomOut} />
          <Cards
            type={type}
            setGroup={setSelectedGroup}
            mapRef={mapRef}
            onFlyToTrees={flyToTrees}
            forestOutlines={forestOutlineFeatures}
          />
        </div>
        <PanelsToggle hidden={panelsHidden} onToggle={() => setPanelsHidden((h) => !h)} />
        <div
          // First click/drag/pinch on the map all begin with a pointerdown.
          onPointerDownCapture={hintDismissed ? undefined : dismissHint}
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
          }}
        >
          <Map
            {...viewState}
            ref={mapRef}
            style={{ width: '100%', height: '100%' }}
            mapStyle={import.meta.env.VITE_MAP_STYLE}
            mapboxAccessToken={import.meta.env.VITE_MAPBOX_TOKEN}
            onMove={(evt) => setViewState(evt.viewState)}
            onLoad={handleMapLoad}
            onClick={handleMapClick}
          >
            {/* Show group layers as soon as initial data is loaded */}
            {isFarmersGroup && farmersGroupData?.features && (
              <FarmetsGroupLayer propsData={farmersGroupData.features} />
            )}
            {(isFactoriesGroup || isFactories) && factoriesData && (
              <FactoriesGroupLayer propsData={factoriesData} />
            )}
            {/* Founder 2026-09-20 (Question 32a): the project group marker is
                hidden on the deforestation card too - that view is polygon,
                cells and legend only. Same title match as the outlines. */}
            {(isForestsGroup || (isSpecificForests && !isMizanZoneCardSelected)) &&
              forestsGroupData && <ForestsGroupLayer propsData={forestsGroupData} />}

            {/* Show detailed layers only when fully loaded */}
            {isFullyLoaded && isFarmers && farmersData && (
              <FarmersLayer propsData={transformedFarmers} />
            )}
            {/* Founder 2026-09-19: no tree pins on the deforestation card. */}
            {showTreePins && treesData && <ForestsLayer propsData={filteredTrees} />}

            {/* D16a - forest outlines (yellow, light fill). Zone polygons are
                Fairchain's planting zones; extents are computed hulls of our
                recorded trees. Founder 2026-09-19: drawn only while the
                "100% Deforestation free" card is selected (was: any forests
                context, which put them on every forests card and the tab). */}
            {isStyleLoaded && isMizanZoneCardSelected && mizan1ZonesData && (
              <OutlineLayer id="planting-zones-mizan1" propsData={mizan1ZonesData} />
            )}
            {/* planting-zones-mizan2 layer disabled 2026-09-17 - single zone only,
                see the query note above. */}
            {isStyleLoaded && isMizanZoneCardSelected && treeExtentsData && (
              <OutlineLayer id="tree-extents" propsData={treeExtentsData} />
            )}
          </Map>
          {/* D16a legend - only entries whose layer actually rendered, so the
              legend never claims a layer that failed or is unpublished (§10).
              Founder 2026-09-19: deforestation card only, like the layers. */}
          {isMizanZoneCardSelected && (hasZoneOutlines || hasExtentOutlines) && (
            <div
              data-testid="forest-outline-legend"
              style={{
                position: 'absolute',
                bottom: 240,
                left: 12,
                background: 'rgba(0,0,0,0.6)',
                color: 'white',
                padding: '6px 10px',
                borderRadius: 4,
                fontSize: 12,
                zIndex: 10,
                maxWidth: 260,
              }}
            >
              {hasZoneOutlines && (
                <LegendZoomButton
                  label="Mizan planting zones"
                  onZoom={() => zoomToOutlines(mizan1ZonesData?.features)}
                />
              )}
              {hasExtentOutlines && (
                <LegendZoomButton
                  label="Extent of recorded trees (Mizan)"
                  onZoom={() => zoomToOutlines(treeExtentsData?.features)}
                />
              )}
              {/* Caveat footer - the wording approved for the original D16a
                  legend entries, moved here when the entries became buttons. */}
              <div style={{ marginTop: 6, fontSize: 11, opacity: 0.85 }}>
                {hasZoneOutlines && <div>Planting zones - source: Fairchain GeoNode</div>}
                {hasExtentOutlines && <div>Extent of recorded trees - not a land boundary</div>}
              </div>
            </div>
          )}
          <div
            style={{
              position: 'absolute',
              bottom: 200,
              left: 12,
              right: 12,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              gap: 6,
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                background: 'rgba(0,0,0,0.6)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              Zoom: {viewState.zoom.toFixed(2)}
            </div>
            {!hintDismissed && hintVariant && <MapHint variant={hintVariant} />}
          </div>
          {selectedGroup && !isFullyLoaded && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                color: '#fff',
                fontSize: '1rem',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            >
              {mapLoadingMessage}
            </div>
          )}
        </div>
      </MapContainer>
      {selectedGroup && (
        <Project
          group={selectedGroup}
          setGroup={setSelectedGroup}
          farmersData={parsedFarmersData}
          treesData={treesData}
          ref={projectSectionRef}
        />
      )}
    </Container>
  );
};
